import { PurchaseActivity } from '@/models/activities';
import { ActivityFeed, BrandMenu, BrandMenuProductLineGroup } from '@/models/shop';
import i18n from '@/plugins/i18n';
import { diffInMinutes } from '@/utils/dates';

const parseField = (field) => {
  if (Number.isNaN(parseFloat(field))) {
    return field;
  }

  return parseFloat(field);
};

/**
 * Build a new activityfeed object for grouping.
 */
const buildActivityGrouping = (event: PurchaseActivity): ActivityFeed => {
  return {
    id: event.id,
    event: event.event,
    brand: event.brand,
    products: [event.product],
    timestamp: event.timestamp,
  };
};

const otherProductLine = {
  // For handling products without product lines
  id: -1,
  isOther: true,
  menuPosition: null,
  name: i18n.t('common.other'),
};

/**
 * Activity feed grouped by brand/event and time.
 * @returns {ActivityFeed[]} the activities grouped.
 */
export function groupedActivity(activities: PurchaseActivity[]): ActivityFeed[] {
  const tempEvents = ['restocked', 'added']; // Temp events. Will be removed in the next sprint.
  const filteredItems = activities.filter((item) => tempEvents.includes(item.event));

  const totalItems = filteredItems.length;

  const brandsActivities = {};

  for (let i = 0; i < totalItems; i++) {
    const brand = brandsActivities[filteredItems[i].brand.id];
    const event = brand && brand[filteredItems[i].event];
    let eventTimeDiffInMin = 0;

    if (event?.length) {
      const eventTimestamp = event[event.length - 1].timestamp;

      eventTimeDiffInMin = diffInMinutes(eventTimestamp, filteredItems[i].timestamp);
    }

    if (!brand) {
      brandsActivities[filteredItems[i].brand.id] = {
        [filteredItems[i].event]: [buildActivityGrouping(filteredItems[i])],
      };
    } else if (brand && !event) {
      brand[filteredItems[i].event] = [buildActivityGrouping(filteredItems[i])];
    } else if (brand && eventTimeDiffInMin >= 2) {
      brand[filteredItems[i].event].push(buildActivityGrouping(filteredItems[i]));
    } else {
      const existingProduct = event[event.length - 1].products.some((p) => p.id === filteredItems[i].product.id);

      if (!existingProduct) {
        event[event.length - 1].products.push(filteredItems[i].product);
      }
    }
  }

  let events = [];

  // return all the activities objects as a flat array.
  for (const index in brandsActivities) {
    for (const event in brandsActivities[index]) {
      events = events.concat(brandsActivities[index][event]);
    }
  }

  return events;
}

/**
 * Parse product
 */
export function parseProduct(product) {
  return {
    ...product,
    displayMaxOrder: parseField(product.displayMaxOrder),
    displayMinOrder: parseField(product.displayMinOrder),
    displayAvailableInventory: parseField(product.displayAvailableInventory),
    quantity: parseField(product.quantity),
    salePrice: product.salePrice
      ? {
          ...product.salePrice,
          amount: parseField(product.salePrice.amount),
        }
      : undefined,
    wholesalePrice: {
      ...product.wholesalePrice,
      amount: parseField(product.wholesalePrice.amount),
    },
    children: product.children ? product.children.map(parseProduct) : undefined,
    productLine: product.productLine ?? otherProductLine,
  };
}

export function parseBrandMenus(menus: BrandMenu[]) {
  return menus.map((menu) => ({
    brand: menu.brand,
    productLines: menu.productLines.map((productLineGroup: BrandMenuProductLineGroup) => ({
      productLine: productLineGroup.productLine || otherProductLine,
      products: productLineGroup.products.map((product) => {
        if (product.children) {
          // If there is only one child product treat it as a standalone
          if (product.children.length === 1) {
            return parseProduct(product.children[0]);
          }

          return {
            ...product,
            children: product.children.map(parseProduct),
          };
        }

        return parseProduct(product);
      }),
    })),
  }));
}
